import React, { useState } from 'react';
import { ApprenticeSelector } from '../components/apprentices/ApprenticeSelector';
import { buildLevels } from '../lib/helpers';

export function WorkplaceSignoffPage({ profile, myApprentices, selApprId, setSelApprId, units, trainingPlans, signoffs, workplaceSignoffs, knowledgeUnits, onToggleWorkplace }) {
  const activeId = profile.role === 'apprentice' ? profile.id : selApprId;
  const plan = trainingPlans.filter(p => p.apprentice_id === activeId);
  const levels = buildLevels(units, plan);

  // Flatten the apprentice's plan units across all year levels
  const planUnits = levels.flatMap(l => l.units);

  // ── A unit is "ready for workplace sign-off" when ──
  //  Knowledge signed off  AND  Evidence signed off  AND  Workplace NOT yet signed off
  const isSignedKnowledge = (code) => knowledgeUnits.some(k => k.apprentice_id === activeId && k.unit_code === code);
  const isSignedEvidence  = (code) => signoffs.some(s => s.apprentice_id === activeId && s.unit_code === code);
  const isSignedWorkplace = (code) => workplaceSignoffs.some(s => s.apprentice_id === activeId && s.unit_code === code);

  const readyUnits = planUnits.filter(u =>
    isSignedKnowledge(u.code) && isSignedEvidence(u.code) && !isSignedWorkplace(u.code)
  );

  // Units the current user signed off already (for a small "recently done" reference)
  const doneUnits = planUnits.filter(u => isSignedWorkplace(u.code));

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Workplace Sign-off</div>
          <div className="page-sub">Units ready for workplace assessment — knowledge and evidence complete</div>
        </div>
      </div>

      {profile.role !== 'apprentice' && (
        <ApprenticeSelector apprentices={myApprentices} active={selApprId} onChange={setSelApprId} />
      )}

      {!activeId ? (
        <div className="empty">
          <div className="empty-icon">👥</div>
          <p>Select an apprentice to see units ready for workplace sign-off.</p>
        </div>
      ) : plan.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📐</div>
          <p>No training plan loaded for this apprentice yet.</p>
        </div>
      ) : readyUnits.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">✅</div>
          <p>No units are currently waiting for workplace sign-off.</p>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
            Units appear here once their knowledge and evidence have both been signed off.
          </p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            {readyUnits.length} unit{readyUnits.length !== 1 ? 's' : ''} ready for sign-off
          </div>
          {readyUnits.map(u => (
            <WorkplaceUnitCard
              key={u.code}
              unit={u}
              apprenticeId={activeId}
              profile={profile}
              onToggleWorkplace={onToggleWorkplace}
            />
          ))}
        </>
      )}

      {/* Reference: units already signed off */}
      {activeId && doneUnits.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.5, marginBottom: 8 }}>
            ALREADY SIGNED OFF ({doneUnits.length})
          </div>
          {doneUnits.map(u => (
            <div key={u.code} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', marginBottom: 6, borderRadius: 6,
              background: 'rgba(39,174,96,.06)', border: '1px solid rgba(39,174,96,.25)',
            }}>
              <span style={{ color: 'var(--green)', fontSize: 14 }}>✓</span>
              <span style={{ fontWeight: 700, fontFamily: 'var(--font-d)', fontSize: 11, letterSpacing: .5, color: 'var(--green)', minWidth: 90 }}>
                {u.code}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text)' }}>{u.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A single unit ready for workplace sign-off, with guide and sign-off button */
function WorkplaceUnitCard({ unit, apprenticeId, profile, onToggleWorkplace }) {
  const [showGuide, setShowGuide] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  async function doSignOff() {
    setSaving(true);
    setErrMsg('');
    try {
      await onToggleWorkplace(apprenticeId, unit.code, true, profile.id);
    } catch (e) {
      console.error('Workplace sign-off failed:', e);
      setErrMsg(e?.message || 'Sign-off failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="unit-card" style={{ marginBottom: 12 }}>
      <div className="uc-header">
        <div>
          <div className="uc-code">{unit.code}</div>
          <div className="uc-name">{unit.name}</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', letterSpacing: 0.5 }}>
          READY
        </span>
      </div>

      {/* Workplace sign-off guide */}
      {unit.workplace_guide && (
        <div style={{ marginTop: 8 }}>
          <button
            className="btn-sm"
            style={{ fontSize: 10, padding: '3px 10px', background: 'rgba(245,166,35,.08)', borderColor: 'var(--accent)', color: 'var(--accent)' }}
            onClick={() => setShowGuide(!showGuide)}
          >
            {showGuide ? '▾ Hide Workplace Guide' : '▸ Workplace Sign-off Guide'}
          </button>
          {showGuide && (
            <div style={{ marginTop: 8, padding: 12, background: 'var(--panel)', borderRadius: 6, border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>
                Before signing off, confirm the apprentice can:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {unit.workplace_guide.split('\n').filter(l => l.trim()).map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.5, color: 'var(--text)' }}>
                    <span style={{ color: 'var(--accent)', flexShrink: 0, fontSize: 11 }}>☐</span>
                    <span>{item.trim()}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)', lineHeight: 1.5, fontStyle: 'italic' }}>
                Only sign off when you have directly observed the apprentice demonstrating these skills in the workshop.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sign-off action */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          className="btn xs green-outline"
          disabled={saving}
          onClick={doSignOff}
        >
          ✓ Sign Off Workplace
        </button>
        {saving && <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }} />}
      </div>
      {errMsg && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: '#3a1a1a', color: '#ffb0b0', borderRadius: 4, fontSize: 13 }}>
          {errMsg}
        </div>
      )}
    </div>
  );
}
